import { useState } from 'react';
import usePWAInstallPrompt from '../../hooks/usePWAInstallPrompt';
import { useWebViewDetection } from '../../hooks/useWebViewDetection';

export default function InstallAppCTA() {
  const { canInstall, isIOS, isStandalone, promptInstall } = usePWAInstallPrompt();
  const { detectedWebView, isPermissiveBrowser, isWebView } = useWebViewDetection();
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [showInstallWarning, setShowInstallWarning] = useState(false);

  if (isStandalone) return null;

  const handleClick = async () => {
    if (canInstall) {
      await promptInstall();
      return;
    }
    if (isIOS && isPermissiveBrowser) {
      setShowIOSHelp((v) => !v);
      setShowInstallWarning(false);
      return;
    }
    // Ambientes sem suporte (WebView ou sem prompt) exibem mensagem universal
    setShowIOSHelp(false);
    setShowInstallWarning((v) => !v);
  };

  return (
    <div className="w-full">
      <button
        onClick={handleClick}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center font-sans px-8 py-4 text-lg font-semibold shadow-card"
      >
        <i className="ri-download-2-line mr-2 text-xl"></i>
        baixe o aplicativo no celular
      </button>

      {showIOSHelp && isIOS && isPermissiveBrowser && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-5 text-left shadow-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-apple-line text-white text-xl" aria-hidden></i>
            </div>
            <div>
              <p className="font-semibold text-blue-900 mb-1">📱 Como instalar no iPhone:</p>
              <p className="text-sm text-blue-900/80">Siga os passos abaixo para adicionar o Magistral à tela inicial.</p>
            </div>
          </div>

          <ol className="space-y-3 text-sm text-gray-700">
            <li className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-blue-700">1</div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">Toque no botão <strong>Compartilhar</strong>.</p>
                <div className="mt-2 flex items-center gap-2 bg-white rounded-lg p-2 border border-gray-200">
                  <div className="w-8 h-8 border-2 border-blue-500 rounded flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="w-5 h-5 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 3v10" />
                      <path d="M8 7l4-4 4 4" />
                      <path d="M5 11v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9" />
                    </svg>
                  </div>
                  <span className="text-xs text-gray-600">Ícone na barra do Safari (quadrado com seta para cima).</span>
                </div>
              </div>
            </li>

            <li className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-blue-700">2</div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">Role e escolha <strong>Adicionar à Tela de Início</strong>.</p>
                <div className="mt-2 flex items-center gap-2 bg-white rounded-lg p-2 border border-gray-200">
                  <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                    <i className="ri-add-box-line text-blue-600 text-lg" aria-hidden></i>
                  </div>
                  <span className="text-xs text-gray-600">Opção disponível no menu de compartilhamento.</span>
                </div>
              </div>
            </li>

            <li className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-blue-700">3</div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">Confirme o nome e toque em <strong>Adicionar</strong>.</p>
                <div className="mt-2 bg-green-50 rounded-lg p-3 border border-green-200">
                  <p className="text-xs text-green-800">✅ Pronto! O ícone do Magistral aparecerá na sua tela inicial.</p>
                </div>
              </div>
            </li>
          </ol>
        </div>
      )}

      {showInstallWarning && (
        <div className="mt-3 text-white/95 text-xs leading-relaxed bg-black/30 rounded-lg p-3 border border-white/20">
          <p className="font-sans" style={{ fontWeight: 600 }}>
            Você está em um navegador limitado
            {detectedWebView ? ` (${detectedWebView})` : ''}
          </p>
          <p className="font-sans" style={{ fontWeight: 500 }}>
            Para instalar o app, abra no navegador principal do seu celular (Safari, Chrome, etc.) clicando no botão abaixo 👇🏻
          </p>
        </div>
      )}
    </div>
  );
}
